import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
// ─── Types ────────────────────────────────────────────────────────────────────
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
// ─── Static data ──────────────────────────────────────────────────────────────
const NAMES = ['María G.', 'Carlos R.', 'Sofía M.', 'Luis H.', 'Ana P.', 'Diego F.', 'Valentina C.', 'Andrés T.', 'Isabella N.', 'Miguel S.', 'Camila V.', 'Javier O.', 'Lucía B.', 'Fernando A.', 'Daniela L.', 'Roberto E.', 'Natalia Q.', 'Alejandro W.', 'Paola K.', 'Sebastián J.', 'Gabriela Z.', 'Ricardo U.', 'Mariana I.', 'Pablo Y.', 'Claudia X.', 'Jorge D.', 'Verónica R.', 'Marcos T.', 'Lorena M.', 'Héctor P.'];
const CITIES = ['Miami, FL', 'Los Angeles, CA', 'New York, NY', 'Houston, TX', 'Chicago, IL', 'Phoenix, AZ', 'San Antonio, TX', 'Dallas, TX', 'San Diego, CA', 'Orlando, FL', 'Las Vegas, NV', 'Denver, CO', 'Atlanta, GA', 'Boston, MA', 'Seattle, WA', 'Tampa, FL', 'Charlotte, NC', 'Austin, TX', 'El Paso, TX', 'Newark, NJ', 'Riverside, CA', 'Fresno, CA', 'Tucson, AZ', 'Sacramento, CA', 'Albuquerque, NM'];
// Fallback catalog — matches the actual page products
const FALLBACK_CATALOG: ScannedProduct[] = [{
  title: 'Reset de Deudas en 14 Días (USA)',
  editionLabel: 'Sistema Principal + 3 Bonos',
  priceNumber: 6.39,
  priceText: '$6.39',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/uhCQ4vNqafhd2Gd8nwDRW3/BONO_3_-_MINI_KIT_PAGO_EXTRA_POR_PAYCHECK_-_PLANTILLA_2B_RITUAL_DE_7_MIN.png'
}, {
  title: 'Filtro Anti-Impulso Amazon',
  editionLabel: 'Mejora Especial',
  priceNumber: 9,
  priceText: '$9',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/kaMUkMJLu6j8wGYV1TUHj7/ORDER_BUMP_1.png'
}, {
  title: 'Escudo Anti-Cajeros',
  editionLabel: 'Mejora Especial',
  priceNumber: 9,
  priceText: '$9',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/qn62UW5gxw4TAPAuq4Sx8i/ORDER_BUMP_2.png'
}];
// ─── Product scanner ──────────────────────────────────────────────────────────
function scanProducts(): ScannedProduct[] {
  const results: ScannedProduct[] = [];
  // 1) Try JSON-LD schema.org Product
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(s => {
      const data = JSON.parse(s.textContent || '{}');
      const items = Array.isArray(data) ? data : [data];
      items.forEach((item: any) => {
        if (item['@type'] === 'Product') {
          const offer = item.offers || {};
          const price = parseFloat(offer.price ?? item.price ?? '0');
          if (price > 0) {
            results.push({
              title: item.name || 'Producto',
              editionLabel: offer.name || item.category || '',
              priceNumber: price,
              priceText: `${offer.priceCurrency || '$'}${price.toFixed(2)}`,
              thumbnailUrl: Array.isArray(item.image) ? item.image[0] : item.image || ''
            });
          }
        }
      });
    });
  } catch (_) {}
  if (results.length > 0) return results;
  // 2) Scan DOM for price-like strings near CTAs
  const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g;
  const ctaKeywords = /add to cart|buy|checkout|order|comprar|añadir|agregar/i;
  const sections = document.querySelectorAll('section, article, [class*="product"], [class*="offer"], [class*="price"]');
  sections.forEach(section => {
    const text = section.textContent || '';
    const hasCTA = ctaKeywords.test(text);
    const priceMatches = [...text.matchAll(priceRegex)];
    if (!hasCTA || priceMatches.length === 0) return;
    const price = parseFloat(priceMatches[0][1]);
    const img = section.querySelector('img');
    const heading = section.querySelector('h1, h2, h3, h4');
    results.push({
      title: heading?.textContent?.trim() || 'Producto',
      editionLabel: '',
      priceNumber: price,
      priceText: `$${price.toFixed(2)}`,
      thumbnailUrl: img?.src || ''
    });
  });
  return results.length > 0 ? results : FALLBACK_CATALOG;
}
// ─── Weighted random product pick ─────────────────────────────────────────────
function pickProduct(catalog: ScannedProduct[]): ScannedProduct {
  // If using fallback, weight first product at 70%
  if (catalog === FALLBACK_CATALOG) {
    return Math.random() < 0.7 ? catalog[0] : catalog[Math.floor(Math.random() * (catalog.length - 1)) + 1];
  }
  return catalog[Math.floor(Math.random() * catalog.length)];
}
// ─── Reduced motion check ─────────────────────────────────────────────────────
function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}
// ─── Toast component ──────────────────────────────────────────────────────────
function Toast({
  toast,
  onDone
}: {
  toast: ToastData;
  onDone: () => void;
}) {
  const reduced = prefersReducedMotion();
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);
  return <motion.div layout initial={reduced ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 40
  }} animate={reduced ? {
    opacity: 1
  } : {
    opacity: 1,
    y: 0
  }} exit={reduced ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 40
  }} transition={{
    duration: reduced ? 0.15 : 0.35,
    ease: 'easeOut'
  }} className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl max-w-xs w-full" style={{
    backgroundColor: 'var(--navy)',
    border: '1px solid rgba(200, 164, 92, 0.35)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.22), 0 0 0 1px rgba(200,164,92,0.1)'
  }} role="status" aria-live="polite" data-id="element-710">
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-white/10" data-id="element-711">
        {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-cover" data-id="element-712" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-713">
            <ShoppingBag className="w-5 h-5" style={{
          color: 'var(--gold)'
        }} data-id="element-714" />
          </div>}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0" data-id="element-715">
        <p className="text-xs font-bold leading-snug" style={{
        color: 'white'
      }} data-id="element-716">
          {toast.name}{' '}
          <span style={{
          color: 'rgba(255,255,255,0.55)',
          fontWeight: 400
        }} data-id="element-717">
            en {toast.city}
          </span>
        </p>
        <p className="text-xs mt-0.5 leading-snug" style={{
        color: 'rgba(255,255,255,0.7)'
      }} data-id="element-718">
          acaba de comprar{' '}
          <span className="font-semibold" style={{
          color: 'var(--gold)'
        }} data-id="element-719">
            {toast.product.editionLabel || toast.product.title}
          </span>
        </p>
        <p className="text-xs mt-0.5 font-bold" style={{
        color: 'var(--gold)'
      }} data-id="element-720">
          {toast.product.priceText}
        </p>
      </div>

      {/* Live dot */}
      <div className="flex-shrink-0 flex flex-col items-center gap-1" data-id="element-721">
        <span className="relative flex h-2 w-2" data-id="element-722">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{
          backgroundColor: 'var(--green)'
        }} data-id="element-723" />
          <span className="relative inline-flex rounded-full h-2 w-2" style={{
          backgroundColor: 'var(--green)'
        }} data-id="element-724" />
        </span>
        <span className="text-[9px] font-medium" style={{
        color: 'rgba(255,255,255,0.35)'
      }} data-id="element-725">
          ahora
        </span>
      </div>
    </motion.div>;
}
// ─── Main component ───────────────────────────────────────────────────────────
export function PurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const catalogRef = useRef<ScannedProduct[]>(FALLBACK_CATALOG);
  const nameHistoryRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    nameHistoryRef.current = [...history.slice(-9), name];
    return name;
  }, []);
  const showNext = useCallback(() => {
    const product = pickProduct(catalogRef.current);
    const name = pickName();
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    setToast({
      id: ++counterRef.current,
      name,
      city,
      product
    });
  }, [pickName]);
  const scheduleNext = useCallback(() => {
    const delay = 8000 + Math.random() * 7000; // 8–15s
    timerRef.current = setTimeout(() => {
      showNext();
    }, delay);
  }, [showNext]);
  const handleDone = useCallback(() => {
    setToast(null);
    scheduleNext();
  }, [scheduleNext]);
  useEffect(() => {
    // Scan products after DOM is ready
    const scanTimer = setTimeout(() => {
      const scanned = scanProducts();
      catalogRef.current = scanned;
    }, 500);
    // First toast after 3s
    timerRef.current = setTimeout(() => {
      showNext();
    }, 3000);
    return () => {
      clearTimeout(scanTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNext]);
  return <div className="fixed bottom-6 left-1/2 z-50 pointer-events-none" style={{
    transform: 'translateX(-50%)'
  }} aria-label="Notificaciones de compras recientes" data-id="element-726">
      <AnimatePresence mode="wait" data-id="element-727">
        {toast && <Toast key={toast.id} toast={toast} onDone={handleDone} data-id="element-728" />}
      </AnimatePresence>
    </div>;
}